import React, { useState, useEffect } from 'react';
import { Box, Button, LinearProgress, Typography, TextField, FormControl, CircularProgress, Checkbox, Container } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { GridColDef, GridToolbarDensitySelector, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarContainer, GridSortModel } from '@mui/x-data-grid';
import useAuth from '../context/AuthProvider';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    AdminHomeDashboardTile,
    BORDER,
    BG_SUBTLE,
    TEXT_PRIMARY,
    TEXT_SECONDARY,
    ACCENT,
} from '../components/Elements/AdminHomeDashboardTile';
import { apiClient } from '../helper/api';
import { FaRegEye, FaDownload, FaPlus, FaTrash, FaFolder, FaFilePdf, FaFile, FaUpload, FaCheckCircle, FaTimesCircle, FaFilePowerpoint, FaFileExcel, FaFileWord, FaFileImage } from 'react-icons/fa';
import { DocumentViewer } from '../components/Elements/DocumentViewer';
import { formatUtcToLocal } from '../helper/utils';
import Swal from 'sweetalert2';
import JSZip from 'jszip';
import AddNewDiligentSearchFolder from '../components/AddEdit/AddNewDiligentSearchFolder';
import { PartnerType, initPartner } from '../models/PartnersModel';
import { LightBlueBtnColor, DarkBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { Months } from '../helper/utils';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import Clear from '@mui/icons-material/Clear';
import Search from '@mui/icons-material/Search';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { RiExpandUpDownLine } from 'react-icons/ri';

export const GoBackIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DiligentClientTileIcon = () => (
    <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16 52V24L32 14L48 24V52H16Z"
            stroke={ACCENT}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M24 52V36H40V52" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DocumentExtensionIconMapper = {
    pdf: <FaFilePdf />,
    doc: <FaFileWord />,
    docx: <FaFileWord />,
    xls: <FaFileExcel />,
    xlsx: <FaFileExcel />,
    ppt: <FaFilePowerpoint />,
    pptx: <FaFilePowerpoint />,
    jpg: <FaFileImage />,
    jpeg: <FaFileImage />,
    png: <FaFileImage />,
    default: <FaFile />,
} as const;

export enum ViewOptions {
    PartnerSelection = 'PartnerSelection',
    FolderSelection = 'FolderSelection',
    DocumentSelection = 'DocumentSelection',
    CreateFolder = 'CreateFolder',
};

export interface DiligentSearchItem {
    partnersId: number;
    customerName: string;
    // isDiligentSearch: boolean;
    // customerPolicyId: number;
    // insuredName: string;
    // homeState: string;
    dateCreated: string; // ISO string
    // policyNumber: string;
    month: number; // 1–12
    year: number;
    // diligentSearchReportID: number | null;
    createdBy: string | null;
    // isCompleted: boolean;
    dateModified: string | null;
    diligentSearchLibraryDocumentId: number;
    documentName: string;
    blob_Address: string;
    fileSizeKB: number;
    // dateUploaded: string;
    isPublished: boolean;
};
export type MonthData = DiligentSearchItem[] | null;
export interface YearData {
    [month: number]: MonthData;
};
export interface DiligentSearchData {
    [year: number]: YearData;
};
export interface DiligentSearchApiResponse {
    status: boolean;
    message: string;
    resp: number;
    data: DiligentSearchData;
};

export const DiligentSearchClientUserRoles: string[] = [
    'Client Admin',
    'Diligent Search User'
];

export enum DiligentSearchPartnerIds{
    Vengroup = 138,
    Ally = 127,
    Intrepid = 134,
};

export const DiligentSearchLibrary: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const userRoles: string[] = user?.userRoles?.split(",").map(role => role.trim()) || [];
    const IsUserClient = Boolean(DiligentSearchClientUserRoles.some(role => userRoles.includes(role)));
    
    const [loading, setLoading] = React.useState(false);
    const [viewLoadingId, setViewLoadingId] = React.useState<number | null>(null);
    const [currentView, setCurrentView] = useState<string | null>(null);
    const [returnView, setReturnView] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [selectedPartner, setSelectedPartner] = useState<PartnerType | null>(initPartner);
    const [partners, setPartners] = useState<PartnerType[]>([initPartner]);
    const IsPartnerSelected = Boolean(selectedPartner && selectedPartner.partnersId !== 0 && selectedPartner !== initPartner);
    const [diligentSearchLibrary, setDiligentSearchLibrary] = useState<DiligentSearchData | null>(null);
    const [originalDiligentSearchLibrary, setOriginalDiligentSearchLibrary] = useState<DiligentSearchData | null>(null);
    const [selectedRows, setSelectedRows] = useState<DiligentSearchItem[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [priorYearsExpanded, setPriorYearsExpanded] = useState<boolean>(false);
    const [selectedPriorYear, setSelectedPriorYear] = useState<number | null>(null);
    const [selectedFolders, setSelectedFolders] = useState<Array<{ year: number; month?: number | null; scope: 'month' | 'year' }>>([]);
    
    // Document viewer state
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerDocuments, setDocumentViewerDocuments] = useState<DiligentSearchItem[]>([]);

    useEffect(() => {
        getDiligentSearchPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // UseEffect to automatically load the library for the logged-in user's partner if they are a client
    useEffect(() => {
        // Wait until the partner list has been loaded from the API — initial state is [initPartner]
        // (partnersId 0), which still has length > 0 and was firing this effect twice: once on mount
        // and again after getDiligentSearchPartners, causing duplicate library fetches and UI flashes.
        const partnerListReady = partners.some((p) => p.partnersId !== 0);
        if (user?.partnersId && partnerListReady) {
            // Automatically load the library for the logged-in user's partner
            if (IsUserClient) {
                getDiligentSearchLibrary();
                setCurrentView(ViewOptions.FolderSelection);
            } else {
                setCurrentView(ViewOptions.PartnerSelection);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partners, user?.partnersId, user?.userRoles]);

    // useEffect(() => {
    //     if(selectedPartner === null || selectedPartner?.partnersId !== 0) {
    //         getDiligentSearchLibrary();
    //     }
    // }, [selectedPartner]);

    //#region API Calls
    const getDiligentSearchPartners = () => {
        setLoading(true);
        apiClient("/DiligentSearch/getPartnersByDiligent", {
            method: "GET",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return
            setPartners(responseJson.data || [initPartner]);
        }).finally(() => {
            setLoading(false);
        });
    };
    
    const getDiligentSearchLibrary = (partner?: PartnerType | null) => {
        // only set partnerToUse if a partner is passed, otherwise set as null
        const partnerToUse = partner !== undefined ? partner : null;

        setDiligentSearchLibrary(null);
        setOriginalDiligentSearchLibrary(null);
        setLoading(true);
        apiClient(`/DiligentSearch/getDiligentSearchLibrary${partnerToUse ? `?partnersId=${partnerToUse.partnersId}` : ''}`, {
            method: "GET",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return
            const diligentSearchData = responseJson.data;
            setDiligentSearchLibrary(diligentSearchData.sortedDiligentSearchData);
            setOriginalDiligentSearchLibrary(diligentSearchData.sortedDiligentSearchData);
            if(!partnerToUse) {
                setSelectedPartner(diligentSearchData.selectedPartner);
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    //#endregion

    const handleSearch = () => {
        if (!searchText.trim() || !originalDiligentSearchLibrary) {
            // If search is empty, restore original data
            setDiligentSearchLibrary(originalDiligentSearchLibrary);
            return;
        }

        const searchLower = searchText.toLowerCase().trim();
        const filteredData: DiligentSearchData = {};

        // Iterate through each year
        Object.keys(originalDiligentSearchLibrary).forEach(yearStr => {
            const year = parseInt(yearStr);
            const yearData = originalDiligentSearchLibrary[year];
            if (!yearData) return;

            const filteredYearData: YearData = {};

            // Iterate through each month
            Object.keys(yearData).forEach(monthStr => {
                const month = parseInt(monthStr);
                const monthData = yearData[month];
                
                // Skip if month data is null or empty
                if (!monthData || monthData.length === 0) return;

                // Filter documents that match the search criteria
                const filteredDocuments = monthData.filter((document: DiligentSearchItem) => {
                    // const insuredName = (document.insuredName || '').toLowerCase();
                    // const policyNumber = (document.policyNumber || '').toLowerCase();
                    // const homeState = (document.homeState || '').toLowerCase();
                    const documentName = (document.documentName || '').toLowerCase();

                    return documentName.includes(searchLower);
                            // insuredName.includes(searchLower) ||
                            // policyNumber.includes(searchLower) ||
                            // homeState.includes(searchLower);
                });

                // Only add month if there are matching documents
                if (filteredDocuments.length > 0) {
                    filteredYearData[month] = filteredDocuments;
                }
            });

            // Only add year if there are matching months
            if (Object.keys(filteredYearData).length > 0) {
                filteredData[year] = filteredYearData;
            }
        });

        setDiligentSearchLibrary(filteredData);
    };

    const handleClear = () => {
        setSearchText('');
        if (originalDiligentSearchLibrary) {
            setDiligentSearchLibrary(originalDiligentSearchLibrary);
        }
    };

    //#region Top level buttons
    const addNewDiligentSearchFolder = () => {
        setReturnView(currentView);
        setCurrentView(ViewOptions.CreateFolder);
    };
    
    const getSelectedFolderDocuments = (): DiligentSearchItem[] => {
        if (selectedFolders.length === 0 || !diligentSearchLibrary) return [];
        const documentsById = new Map<number, DiligentSearchItem>();
        selectedFolders.forEach((folder) => {
            const yearData = diligentSearchLibrary[folder.year];
            if (!yearData) return;
            if (folder.scope === 'year') {
                Object.values(yearData)
                    .filter((monthData): monthData is DiligentSearchItem[] => Array.isArray(monthData))
                    .forEach((monthDocs) => {
                        monthDocs.forEach((doc) => documentsById.set(doc.diligentSearchLibraryDocumentId, doc));
                    });
                return;
            }
            const monthData = yearData?.[folder.month as number];
            if (Array.isArray(monthData)) {
                monthData.forEach((doc) => documentsById.set(doc.diligentSearchLibraryDocumentId, doc));
            }
        });
        return Array.from(documentsById.values());
    };

    const getSelectedFolderLabel = () => {
        if (selectedFolders.length === 0) return '';
        if (selectedFolders.length === 1) {
            const folder = selectedFolders[0];
            if (folder.scope === 'year') {
                return `${folder.year}`;
            }
            return `${Months[folder.month as keyof typeof Months]} ${folder.year}`;
        }
        return `${selectedFolders.length} folders`;
    };

    const deleteSelectedDiligentSearch = () => {
        const folderRows = currentView === ViewOptions.FolderSelection ? getSelectedFolderDocuments() : [];
        const rowsToDelete = folderRows.length > 0 ? folderRows : selectedRows;
        const isFolderDelete = currentView === ViewOptions.FolderSelection && folderRows.length > 0;
        if(rowsToDelete.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'No Documents Selected',
                text: 'Please select at least one document to delete.',
            });
            return;
        }
        setLoading(true);
        Swal.fire({
            title: '<div style="color: white;">Are You Sure?</div>',
            html: isFolderDelete
                ? `<div style="color: white;">This will delete all documents from <span style="color: yellow;">${getSelectedFolderLabel()}</span></div>`
                : '<div style="color: white;">This will delete all selected documents from the library</div>',
            background: PrimaryBlueColor,
            showCancelButton: true,
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                // call api to delete the documents
                apiClient(`/DiligentSearch/deleteDiligentSearchDocuments?partnersId=${selectedPartner?.partnersId}`, {
                    method: "POST",
                    headers: new Headers({
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    }),
                    body: JSON.stringify({
                        diligentSearchLibraryDocumentIds: rowsToDelete.map(row => row.diligentSearchLibraryDocumentId),
                    }),
                }).then((responseJson) => {
                    if (!responseJson) return
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'The documents have been deleted successfully.',
                    });
                    // refresh the library
                    getDiligentSearchLibrary(selectedPartner);
                    setSelectedRows([]);
                    if (isFolderDelete) {
                        setSelectedFolders([]);
                    }
                }).finally(() => {
                    setLoading(false);
                }).catch((error) => {
                    console.error('Error in deleteSelectedDiligentSearch:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'An error occurred while deleting the documents.',
                    });
                    setLoading(false);
                });
            }
        }).catch((error) => {
            console.error('Error in deleteSelectedDiligentSearch:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while deleting the documents.',
            });
            setLoading(false);
        }).finally(() => {
            setLoading(false);
        });
    };

    const downloadSelectedDiligentSearchDocuments = async (allDocuments: boolean = false, rowsToDownloadOverride?: DiligentSearchItem[]) => {
        // Determine which rows to download
        let rowsToDownload: DiligentSearchItem[];
        
        if (rowsToDownloadOverride && rowsToDownloadOverride.length > 0) {
            // Use the provided rows (e.g., from action column click)
            setSelectedRows(rowsToDownloadOverride);
            rowsToDownload = rowsToDownloadOverride;
        } else if (allDocuments) {
            // Use all documents
            rowsToDownload = documents;
            
            // Check if there are any documents available
            if (rowsToDownload.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Available',
                    text: 'There are no documents available to download.',
                });
                return;
            }
        } else {
            // When downloading selected documents, check if any are selected
            // Check both that selectedRows exists and has items
            const hasSelectedRows = selectedRows && Array.isArray(selectedRows) && selectedRows.length > 0;
            if (!hasSelectedRows) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Selected',
                    text: 'Please select at least one document to download.',
                });
                return;
            }
            rowsToDownload = selectedRows;
        }
        
        // create a folder with the selected documents
        const customerName = selectedPartner?.customerName || 'Diligent Search Library';
        const isFolderDownload = currentView === ViewOptions.FolderSelection && selectedFolders.length > 0;
        const folderName = isFolderDownload
            ? `${customerName} - Diligent Search Library Documents`
            : `${customerName} - ${Months[selectedMonth as keyof typeof Months]} ${selectedYear}`;
        
        const confirmResult = await Swal.fire({
            title: '<div style="color: white;">Download Documents</div>',
            html: `<div style="color: white;">This will download all${allDocuments ? '' : ' selected'} documents to a zip file named <strong>${folderName}.zip</strong></div>`,
            showCancelButton: true,
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        });
        
        if (!confirmResult.isConfirmed) {
            setSelectedRows([]);
            return;
        }
        
        setLoading(true);
        
        // Show loading dialog
        Swal.fire({
            title: 'Preparing Download...',
            html: 'Please wait while we prepare your files for download.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
        
        try {
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;
            const errors: string[] = [];
            const usedFileNames = new Set<string>();

            // Helper function to generate unique filename (Chrome-style numbering)
            const generateUniqueFileName = (baseFileName: string): string => {
                const fileExtension = baseFileName.includes('.') 
                    ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                    : '';
                const fileNameWithoutExt = baseFileName.includes('.')
                    ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                    : baseFileName;
                
                let uniqueFileName = baseFileName;
                let counter = 0;
                
                // Keep trying until we find a unique name
                while (usedFileNames.has(uniqueFileName)) {
                    counter++;
                    uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                }
                
                usedFileNames.add(uniqueFileName);
                return uniqueFileName;
            };

            // Generate unique filenames for all files first
            const filesWithUniqueNames = rowsToDownload.map(row => ({
                row,
                uniqueFileName: generateUniqueFileName(row.documentName || `document_${row.diligentSearchLibraryDocumentId}`)
            }));

            // Fetch each file and add to zip
            await Promise.all(
                filesWithUniqueNames.map(async ({ row, uniqueFileName }) => {
                    try {
                        // Fetch the file from blob_Address
                        const response = await fetch(row.blob_Address, {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'omit',
                            cache: 'no-cache',
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }

                        // Get the blob (preserves original content type)
                        const blob = await response.blob();
                        
                        // Add file to zip with unique name
                        zip.file(uniqueFileName, blob);
                        successCount++;
                    } catch (error: any) {
                        failCount++;
                        const errorMsg = error.message || 'Failed to download';
                        errors.push(`${row.documentName}: ${errorMsg}`);
                        console.error(`Error downloading ${row.documentName} from ${row.blob_Address}:`, error);
                        console.error('Error details:', error);
                    }
                })
            );

            if (successCount === 0) {
                throw new Error('Failed to download any documents');
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folderName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.close();
            
            // Show success/partial success message
            if (failCount > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Partial Success',
                    html: `
                        <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                        <p><strong>${failCount}</strong> document(s) failed to download.</p>
                        ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                    `,
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `All ${successCount} document(s) have been downloaded successfully.`,
                    confirmButtonText: 'OK',
                });
            }
        } catch (error: any) {
            console.error('Error in downloadSelectedDiligentSearchDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
            setSelectedRows([]);
        }
    };

    const publishDiligentSearchDocuments = () => {
        if(selectedRows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'No Documents Selected',
                text: 'Please select at least one document to publish.',
            });
            return;
        }
        setLoading(true);
        Swal.fire({
            title: '<div style="color: white;">Are You Sure?</div>',
            html: `<div style="color: white;">This will publish all uploaded documents to the Client Portal for <span style="color: yellow;">${selectedPartner?.customerName}</span></div>`,
            showCancelButton: true,
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
            confirmButtonText: 'Yes, Proceed to PUBLISH',
            cancelButtonText: 'No, Continue to EDIT',
        }).then((result) => {
            if (result.isConfirmed) {
                // call api to publish the documents
                apiClient(`/DiligentSearch/publishDiligentSearchDocuments?partnersId=${selectedPartner?.partnersId}`, {
                    method: "POST",
                    headers: new Headers({
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    }),
                    body: JSON.stringify({
                        diligentSearchLibraryDocumentIds: selectedRows.map(row => row.diligentSearchLibraryDocumentId),
                    }),
                }).then((responseJson) => {
                    if (!responseJson) return
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'The documents have been published successfully.',
                    });
                    // refresh the library
                    getDiligentSearchLibrary(selectedPartner);
                    setSelectedRows([]);
                }).catch((error) => {
                    console.error('Error in publishDiligentSearchDocuments:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'An error occurred while publishing the documents.',
                    });
                });
            }
        }).finally(() => {
            setLoading(false);
            setSelectedRows([]);
        }).catch((error) => {
            console.error('Error in publishDiligentSearchDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while publishing the documents.',
            });
            setLoading(false);
        });
    };
    //#endregion

    // Get documents for the selected month/year
    const getDocumentsForSelectedFolder = (): DiligentSearchItem[] => {
        if (selectedYear === null || selectedMonth === null || !diligentSearchLibrary) {
            return [];
        }
        const yearData = diligentSearchLibrary[selectedYear];
        if (!yearData) return [];
        const monthData = yearData[selectedMonth];
        return monthData || [];
    };

    const documents = getDocumentsForSelectedFolder();

    const handleViewDocuments = (docs: DiligentSearchItem[]) => {
        const withBlob = docs.filter((d) => d.blob_Address);
        if (withBlob.length === 0) return;
        // For multiple documents, use the first document's ID for loading state
        const firstDocId = withBlob[0]?.diligentSearchLibraryDocumentId;
        if (firstDocId) {
            setViewLoadingId(firstDocId);
        }
        // Simulate a small delay to show loading state, then open viewer
        setTimeout(() => {
            setDocumentViewerDocuments(withBlob);
            setDocumentViewerOpen(true);
            setViewLoadingId(null);
        }, 300);
    };

    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setDocumentViewerDocuments([]);
    };

    const FileTypeIconMapper = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        return DocumentExtensionIconMapper[extension as keyof typeof DocumentExtensionIconMapper] || DocumentExtensionIconMapper.default || <FaFile />;
    };
    const columns: GridColDef[] = [
        { field: 'documentName', headerName: 'Document Name', 
            flex: 0.3, width: 100, minWidth: 200,
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'documentName'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className={`table-card-title ${IsUserClient ? 'text-white' : ''}`}>{params.colDef.headerName}</span>
                        {IsUserClient ? (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title" style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px', color:  '#FFFFFF'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"  style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#FFFFFF'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        ) : (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        )}
                    </div>
                )
            },
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        {FileTypeIconMapper(params.value)}
                    </Box>
                    <Typography sx={{ fontFamily: "'Proxima Nova', sans-serif" }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        // only show this column if the user is a client
        ...(!IsUserClient ? [{
            field: 'isPublished', headerName: 'Published',
            width: 150, align: 'left' as const, headerAlign: 'left' as const,
            hideSortIcons: true,
            renderHeader: (params: any) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'isPublished'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className={`table-card-title ${IsUserClient ? 'text-white' : ''}`}>{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                    </div>
                )
            },
            renderCell: (params: any) => {
                return params.value ? <FaCheckCircle color="green" /> : <FaTimesCircle color="red" />;
            },
        }] : []),
        // {
        //     field: 'insuredName', headerName: 'Insured Name',
        //     flex: 2,
        //     minWidth: 150,
        //     renderCell: (params) => {
        //         if (!params.value) return null;
        //         return params.value;
        //     },
        // },
        // {
        //     field: 'policyNumber', headerName: 'Policy Number', 
        //     // flex: 2,
        //     minWidth: 120,
        //     renderCell: (params) => {
        //         if (!params.value) return null;
        //         return params.value;
        //     },
        // },
        // {
        //     field: 'homeState', headerName: 'Home State',
        //     width: 100,
        //     align: 'center' as const,
        //     renderCell: (params) => {
        //         if (!params.value) return null;
        //         return params.value;
        //     },
        // },
        { field: 'dateModified', headerName: 'Date Modified', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'dateModified'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className={`table-card-title ${IsUserClient ? 'text-white' : ''}`}>{params.colDef.headerName}</span>
                        {IsUserClient ? (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title" style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px', color: '#FFFFFF'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"  style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#FFFFFF'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        ) : (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        )}
                    </div>
                )
            },
            width: 175, align: 'left' as const, headerAlign: 'left' as const,
            renderCell: (params) => formatUtcToLocal(params.value, 'MM-DD-YYYY hh:mm A'),
        },
        { field: 'fileSizeKB', headerName: 'Size', 
            width: 175, align: 'left' as const, headerAlign: 'left' as const,
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'fileSizeKB'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className={`table-card-title ${IsUserClient ? 'text-white' : ''}`}>{params.colDef.headerName}</span>
                        {IsUserClient ? (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title" style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px', color:  '#FFFFFF'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"  style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#FFFFFF'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        ) : (
                            <>
                                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                            </>
                        )}
                        {/* {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title" style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px', color:  '#FFFFFF'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"  style={{color: '#ffffff80'}}><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#FFFFFF'}} /></span> : <span className=""><RiExpandUpDownLine /></span>} */}
                    </div>
                )
            },
            renderCell: (params) => {
                if (!params.value) return null;
                return `${params.value} KB`;
            },
        },
        /*
        { field: 'actions', headerName: 'Action',
            width: 120, sortable: false, filterable: false,
            headerAlign: 'center' as const, align: 'center' as const,
            renderCell: (params) => {
                return (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        justifyContent: 'center',
                    }}>
                        <Tooltip title="View Document" arrow={true}>
                            <IconButton title="View Document"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDocument(params.row);
                                }}
                                disabled={viewLoadingId === params.row.diligentSearchLibraryDocumentId}
                                sx={{ color: LightBlueBtnColor }}
                            >
                                {viewLoadingId === params.row.diligentSearchLibraryDocumentId ? (
                                    <CircularProgress size={20} sx={{ color: LightBlueBtnColor }} />
                                ) : (
                                    <FaRegEye />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download" arrow={true}>
                            <IconButton title="Download"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSelectedDiligentSearchDocuments(false, [params.row]);
                                }}
                                sx={{ color: LightBlueBtnColor }}
                            ><FaDownload /></IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
        */
    ];

    const onSelectFolder = (year: number, month: number) => {
        setSelectedYear(year);
        setSelectedMonth(month);
        setPriorYearsExpanded(false);
        setSelectedPriorYear(null);
        setSelectedFolders([]);
        setCurrentView(ViewOptions.DocumentSelection);
    };
    
    const onSelectPriorYear = (year: number) => {
        setSelectedPriorYear(year);
        setPriorYearsExpanded(true);
        setSelectedFolders([]);
    };
    
    const onSelectPriorYearsFolder = () => {
        setPriorYearsExpanded(true);
        setSelectedPriorYear(null);
        setSelectedYear(null);
        setSelectedMonth(null);
        setSelectedFolders([]);
    };
    
    /** Close the upload folder dialog and refresh the library
     * @param month - the month to select
     * @param year - the year to select
     */
    const onCloseUploadFolder = (month?: number | null, year?: number | null) => {
        if (month && year) {
            getDiligentSearchLibrary(selectedPartner);
            setSelectedYear(year);
            setSelectedMonth(month);
            setSelectedFolders([{ year, month, scope: 'month' }]);
            setCurrentView(ViewOptions.DocumentSelection);
        } else {
            setCurrentView(returnView ?? ViewOptions.FolderSelection);
        }
        setReturnView(null);
    };
    
    const handleToggleFolderSelection = (year: number, month?: number | null, scope: 'month' | 'year' = 'month') => {
        const normalizedMonth = month ?? null;
        const isSelected = selectedFolders.some(
            (folder) => folder.year === year && folder.month === normalizedMonth && folder.scope === scope
        );
        setSelectedFolders((prev) => {
            if (isSelected) {
                return prev.filter((folder) => !(folder.year === year && folder.month === normalizedMonth && folder.scope === scope));
            }
            return [...prev, { year, month: normalizedMonth, scope }];
        });
        setSelectedRows([]);
    };

    const FolderTextStyle = {
        fontSize: '16px',
        color: 'black',  // '#424242'
        fontFamily: "'Proxima Nova', sans-serif",
        fontWeight: 600,
    };

    const BreadcrumbNavigation = () => {
        const breadcrumbTextStyle = { 
            fontSize: '16px', 
            color: '#757575', 
            fontFamily: "'Proxima Nova', sans-serif", 
            fontWeight: 600 
        };
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography onClick={() => {
                        setSelectedYear(null);
                        setSelectedMonth(null);
                        setSelectedRows([]);
                        setPriorYearsExpanded(false);
                        setSelectedPriorYear(null);
                        setSelectedFolders([]);
                        setCurrentView(ViewOptions.FolderSelection);
                    }}
                    sx={{
                        fontSize: '16px',
                        paddingLeft: '20px',
                        color: selectedYear === null && selectedMonth === null && !priorYearsExpanded ? '#424242' : LightBlueBtnColor,
                        fontFamily: "'Proxima Nova', sans-serif",
                        fontWeight: 600,
                        cursor: selectedYear === null && selectedMonth === null && !priorYearsExpanded ? 'default' : 'pointer',
                        '&:hover': {
                            textDecoration: selectedYear === null && selectedMonth === null && !priorYearsExpanded ? 'none' : 'underline',
                        },
                    }}
                >{selectedPartner?.customerName}</Typography>
                {priorYearsExpanded && selectedPriorYear === null && (<>
                    <Typography sx={breadcrumbTextStyle}>/</Typography>
                    <Typography sx={FolderTextStyle}>Prior Years</Typography>
                </>)}
                {selectedPriorYear !== null && (<>
                    <Typography sx={breadcrumbTextStyle}>/</Typography>
                    <Typography onClick={() => {
                            setSelectedPriorYear(null);
                            setSelectedYear(null);
                            setSelectedMonth(null);
                        }}
                        sx={{
                            ...FolderTextStyle,
                            color: LightBlueBtnColor,
                            cursor: 'pointer',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >Prior Years</Typography>
                    <Typography sx={breadcrumbTextStyle}>/</Typography>
                    <Typography sx={FolderTextStyle}>
                        {selectedPriorYear}
                    </Typography>
                </>)}
                {selectedYear !== null && selectedMonth !== null && (<>
                    <Typography sx={breadcrumbTextStyle}>/</Typography>
                    <Typography sx={FolderTextStyle}>
                        {Months[selectedMonth as keyof typeof Months]} {selectedYear}
                    </Typography>
                </>)}
            </Box>
        );
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                width: '100%',
                padding: '8px 0',
            }}>
                <BreadcrumbNavigation />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CustomToolbar />
                </Box>
            </Box>
        );
    };

    if(!IsUserClient && (selectedPartner === null || selectedPartner?.partnersId === 0)) {
        return (
            <Box
                sx={{
                    minHeight: { xs: 'calc(100vh - 85px)', md: 'calc(100vh - 250px)' },
                    backgroundColor: BG_SUBTLE,
                    paddingTop: { xs: '40px', md: '84px' },
                    marginLeft: { xs: '-24px', md: '0px' },
                    marginRight: { xs: '-24px', md: '0px' },
                    paddingBottom: 3,
                }}
            >
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            borderBottom: `1px solid ${BORDER}`,
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            mb: 3,
                            px: 3,
                            py: 2.5,
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                            border: `1px solid ${BORDER}`,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                color: TEXT_PRIMARY,
                                mb: 0.25,
                            }}
                        >
                            Diligent Search Library
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.9375rem',
                                fontWeight: 400,
                                color: TEXT_SECONDARY,
                            }}
                        >
                            Select a client to access folders and documents.
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            mb: 2,
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: TEXT_PRIMARY,
                        }}
                    >
                        Choose a client
                    </Typography>
                    {!loading && (
                        <Box
                            sx={{
                                display: 'grid',
                                width: '100%',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(2, minmax(0, 370px))',
                                },
                                justifyContent: 'center',
                            }}
                        >
                            {partners
                                .filter((client) => client.partnersId !== 0)
                                .map((client) => (
                                    <Box key={client.partnersId} sx={{ minWidth: 0 }}>
                                        <AdminHomeDashboardTile
                                            title={client.customerName ?? ''}
                                            icon={<DiligentClientTileIcon />}
                                            onClick={() => {
                                                setSelectedPartner(client);
                                                getDiligentSearchLibrary(client);
                                                setSelectedFolders([]);
                                                setCurrentView(ViewOptions.FolderSelection);
                                            }}
                                            iconBgColor="#f0f9ff"
                                        />
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Container>
            </Box>
        );
    }
    const InlineFolderIcon = ({ selected = false }: { selected?: boolean }) => (
        <Box
            component="span"
            sx={{
                width: '34px',
                height: '34px',
                mr: 1.5,
                borderRadius: '10px',
                border: selected ? `1px solid ${LightBlueBtnColor}` : '1px solid rgba(32, 153, 210, 0.25)',
                background: selected ? 'rgba(32, 153, 210, 0.16)' : 'rgba(32, 153, 210, 0.08)',
                color: selected ? DarkBlueBtnColor : LightBlueBtnColor,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
            }}
        >
            <FaFolder size={14} />
        </Box>
    );
    const ModernActionButton = ({
        label,
        subLabel,
        icon,
        onClick,
        disabled = false,
        tone = 'primary',
        minWidth = 'auto',
    }: {
        label: string;
        subLabel?: string;
        icon: React.ReactNode;
        onClick: () => void;
        disabled?: boolean;
        tone?: 'primary' | 'danger';
        minWidth?: string;
    }) => (
        <Button
            onClick={onClick}
            disabled={disabled}
            startIcon={icon}
            sx={{
                minWidth,
                height: '44px',
                borderRadius: '4px',
                textTransform: 'none',
                fontFamily: "'Proxima Nova', sans-serif",
                fontWeight: 600,
                fontSize: '14px',
                px: 4,
                color: WhiteColor,
                backgroundColor: tone === 'danger' ? '#C54844' : '#2099d2',
                boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
                '& .MuiButton-startIcon': {
                    marginRight: subLabel ? '6px' : '8px',
                    fontSize: '12px',
                    marginBottom: 'auto',
                    marginTop: '3px'
                },
                '& .MuiButton-startIcon svg': {
                    fontSize: '14px'
                },
                '&:hover': {
                    backgroundColor: tone === 'danger' ? '#A53632' : '#176990',
                    boxShadow: tone === 'danger'
                        ? '0px 2px 4px -1px rgba(185, 63, 57, 0.30),0px 4px 5px 0px rgba(185, 63, 57, 0.30),0px 1px 10px 0px rgba(185, 63, 57, 0.30)'
                        : '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                },
                '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: '#8FA0AA',
                    boxShadow: 'none',
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.05 }}>
                <span>{label}</span>
                {subLabel && (
                    <span style={{ fontSize: '11px', fontWeight: 600, opacity: 0.95, marginTop: '2px' }}>{subLabel}</span>
                )}
            </Box>
        </Button>
    );
    const CreateFolderButton = () => (
        <Box sx={{ /* minWidth: '170px' */ }}>
            <ModernActionButton
                label="Create Folder"
                subLabel="Add New"
                icon={<FaPlus />}
                onClick={addNewDiligentSearchFolder}
            />
        </Box>
    );
    const ViewButton = () => {
        const isAnySelectedLoading = selectedRows.some(row => viewLoadingId === row.diligentSearchLibraryDocumentId);
        return (
            <Box sx={{ /* minWidth: '170px' */ }}>
                <ModernActionButton
                    label={isAnySelectedLoading ? 'Loading...' : 'View'}
                    subLabel={isAnySelectedLoading ? 'Please Wait' : 'Selected'}
                    icon={isAnySelectedLoading ? <CircularProgress size={14} sx={{ color: WhiteColor }} /> : <FaRegEye />}
                    onClick={() => handleViewDocuments(selectedRows)}
                    disabled={selectedRows.length === 0 || isAnySelectedLoading}
                />
            </Box>
        );
    };
    const DeleteButton = () => {
        const folderRows = currentView === ViewOptions.FolderSelection ? getSelectedFolderDocuments() : [];
        const hasSelection = currentView === ViewOptions.FolderSelection ? folderRows.length > 0 : selectedRows.length > 0;
        return (
            <Box sx={{ /* minWidth: '170px' */ }}>
                <ModernActionButton
                    label="Delete"
                    subLabel="Selected"
                    icon={<FaTrash />}
                    tone="danger"
                    onClick={deleteSelectedDiligentSearch}
                    disabled={!hasSelection}
                />
            </Box>
        );
    };
    const DownloadButton = () => {
        const folderRows = currentView === ViewOptions.FolderSelection ? getSelectedFolderDocuments() : [];
        const hasSelection = currentView === ViewOptions.FolderSelection ? folderRows.length > 0 : selectedRows.length > 0;
        return (
        <Box sx={{ /* minWidth: IsUserClient ? '190px' : '170px' */ }}>
            <ModernActionButton
                label="Download"
                subLabel={IsUserClient ? 'Selected Items' : 'Selected'}
                icon={<FaDownload />}
                onClick={() => {
                    if (currentView === ViewOptions.FolderSelection) {
                        downloadSelectedDiligentSearchDocuments(false, folderRows);
                        return;
                    }
                    downloadSelectedDiligentSearchDocuments(false);
                }}
                disabled={!hasSelection}
            />
        </Box>
        );
    };
    const DownloadAllButton = () => (
        <Box sx={{ /* minWidth: '170px' */ }}>
            <ModernActionButton
                label="Download All"
                subLabel="All Items"
                icon={<FaDownload />}
                onClick={() => {
                    // select all documents visible to the client user
                    setSelectedRows(documents);
                    downloadSelectedDiligentSearchDocuments(true);
                }}
            />
        </Box>
    );
    const PublishButton = () => (
        <Box sx={{ /* minWidth: '170px' */ }}>
            <ModernActionButton
                label="Publish"
                subLabel="Selected"
                icon={<FaUpload />}
                onClick={publishDiligentSearchDocuments}
                disabled={selectedRows.length === 0}
            />
        </Box>
    );
    const IsShowButtons = {
        CreateFolder: Boolean((currentView === ViewOptions.FolderSelection || currentView === ViewOptions.CreateFolder) && !IsUserClient),
        View: Boolean(currentView === ViewOptions.DocumentSelection),
        Delete: Boolean((currentView === ViewOptions.DocumentSelection || currentView === ViewOptions.FolderSelection || currentView === ViewOptions.CreateFolder) && !IsUserClient),
        Download: Boolean(currentView === ViewOptions.DocumentSelection || ((currentView === ViewOptions.FolderSelection || currentView === ViewOptions.CreateFolder) && !IsUserClient)),
        DownloadAll: Boolean(currentView === ViewOptions.DocumentSelection && IsUserClient),
        Publish: Boolean(currentView === ViewOptions.DocumentSelection && !IsUserClient),
    };
    type FolderItemProps = {
        label: React.ReactNode;
        onClick: () => void;
        mt?: number;
        selectable?: boolean;
        selected?: boolean;
        onSelect?: () => void;
    };
    const FolderItem: React.FC<FolderItemProps> = ({ label, onClick, mt = 0, selectable = false, selected = false, onSelect }) => (
        <Box>
            <Box onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.2,
                    px: 2.2,
                    cursor: 'pointer',
                    mt,
                    // borderRadius: '14px',
                    borderTop: selected ? `1px solid ${LightBlueBtnColor}` : '1px solid rgba(22, 46, 60, 0.12)',
                    background: selected
                        ? 'linear-gradient(90deg, rgba(32, 153, 210, 0.12) 0%, rgba(32, 153, 210, 0.05) 100%)'
                        : WhiteColor,
                    boxShadow: selected ? '0 8px 18px rgba(22, 106, 151, 0.14)' : '0 4px 12px rgba(19, 45, 61, 0.06)',
                    transition: 'all 0.22s ease',
                    '&:hover': {
                        // borderColor: selected ? LightBlueBtnColor : 'rgba(22, 46, 60, 0.25)',
                        background: selected
                            ? 'linear-gradient(90deg, rgba(32, 153, 210, 0.14) 0%, rgba(32, 153, 210, 0.06) 100%)'
                            : '#F7FAFC',
                        // boxShadow: '0 10px 20px rgba(22, 46, 60, 0.10)',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {(!IsUserClient && selectable) && (
                        <Checkbox
                            checked={selected}
                            onChange={(event) => {
                                event.stopPropagation();
                                onSelect?.();
                            }}
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                                p: 0,
                                mr: 1.2,
                                color: LightBlueBtnColor,
                                '&.Mui-checked': { color: LightBlueBtnColor },
                            }}
                        />
                    )}
                    <InlineFolderIcon selected={selected} />
                    <Typography sx={{ ...FolderTextStyle, color: selected ? DarkBlueBtnColor : '#162E3C' }}>{label}</Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: selected ? DarkBlueBtnColor : '#6A7C88',
                        fontFamily: "'Proxima Nova', sans-serif",
                        fontWeight: 600,
                        letterSpacing: '0.3px',
                        textTransform: 'uppercase',
                    }}
                >
                    Folder
                </Typography>
            </Box>
        </Box>
    );
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box >
                <Box sx={{
                    // background: 'linear-gradient(135deg, #E9F7FF 0%, #F6FBFF 100%)',
                    // border: '1px solid rgba(22, 46, 60, 0.08)',
                    borderBottom: '1px solid #e2e8f0',
                    // borderRadius: '16px',
                    // px: { xs: 2, sm: 3 },
                    // py: 2.5,
                    // mb: 2.5,
                    pb: 1.5,
                    display: 'flex',
                    // flexDirection: { xs: 'column', sm: 'row' },
                    flexDirection: 'column',
                    // gap: 1.5,
                    // alignItems: { xs: 'flex-start', sm: 'center' },
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    backgroundColor: '#fafbfc',
                    marginLeft: { xs: '-24px', lg: '0px' },
                    marginRight: { xs: '-24px', lg: '0px' },
                    px: { xs: 4, lg: 2 },
                    pt: { xs: 2, lg: 2 },
                    py: 2,
                }}>
                    {IsUserClient ? (
                        <Typography
                            sx={{
                                fontSize: { xs: '24px', sm: '28px' },
                                fontWeight: 700,
                                color: '#132D3D',
                                fontFamily: "'Proxima Nova', sans-serif",
                                lineHeight: 1.1,
                            }}
                        >
                            Diligent Search Library
                        </Typography>
                    ) : (
                        <Typography
                            onClick={() => { 
                                setDiligentSearchLibrary(null);
                                setOriginalDiligentSearchLibrary(null);
                                setSelectedPartner(initPartner); 
                                setSelectedYear(null);
                                setSelectedMonth(null);
                                setSelectedRows([]);
                                setPriorYearsExpanded(false);
                                setSelectedPriorYear(null);
                                setSelectedFolders([]);
                                setCurrentView(ViewOptions.PartnerSelection);
                            }}
                            variant="h6" 
                            // className='card-title' 
                            // sx={{ py: 2, }}
                            style={{ fontSize: '14px', fontWeight: 600, color: '#132D3D', fontFamily: "'Proxima Nova', sans-serif", lineHeight: 1.1 }}
                        >
                            <ArrowBackOutlinedIcon sx={{ fontSize: '14px' }} /> Diligent Search Library</Typography>
                    )}
                    <Box sx={{
                        // px: 1.5,
                        // py: 0.6,
                        // borderRadius: '999px',
                        // border: '1px solid rgba(32, 153, 210, 0.35)',
                        // backgroundColor: 'rgba(32, 153, 210, 0.14)',
                        // color: '#0C4967',
                        color: '#132D3D',
                        // fontFamily: "'Proxima Nova', sans-serif",
                        fontWeight: 400,
                        fontSize: '16px',
                    }}>
                        {selectedPartner?.customerName}
                    </Box>
                </Box>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            
            <Box sx={{
                width: "100%",
                borderRadius: '16px',
                marginTop: '15px',
                border: '1px solid rgba(22, 46, 60, 0.10)',
                backgroundColor: WhiteColor,
                boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
                p: { xs: 1.5, sm: 2.5 },
            }}>
                <Box sx={{ width: '100%', flexDirection: { xs: 'column', lg: 'row' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {(IsUserClient && selectedYear === null && selectedMonth === null && IsPartnerSelected) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', paddingLeft: '3px', }}>
                            <Typography sx={{ ...FolderTextStyle, fontSize: '18px', color: '#1A4E69' }}>Please select a month below to access the diligent search documents.</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* {!IsUserClient && <CreateFolderButton />}
                        {currentView === ViewOptions.DocumentSelection && (<>
                            {!IsUserClient && <DeleteButton />}
                            {IsUserClient && <ViewButton />}
                            <DownloadButton />
                            {IsUserClient && <DownloadAllButton />}
                            {!IsUserClient && <PublishButton />}
                        </>)} */}
                        {IsShowButtons.CreateFolder && <CreateFolderButton />}
                        {IsShowButtons.View && <ViewButton />}
                        {IsShowButtons.Delete && <DeleteButton />}
                        {IsShowButtons.Download && <DownloadButton />}
                        {IsShowButtons.DownloadAll && <DownloadAllButton />}
                        {IsShowButtons.Publish && <PublishButton />}
                    </Box>
                    
                    {/* Search Section */}
                    {(currentView === ViewOptions.DocumentSelection || currentView === ViewOptions.FolderSelection) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, ml: 'auto', mr: { xs: 'auto', lg: '0px' }, mt: { xs: '10px', lg: '0px' }, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                        <FormControl sx={{ width: { xs: '100%', sm: 280, md: '320px' }, minWidth: 0, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                            <TextField 
                                // label="Search Insured Name, Policy Number..."
                                label="Search Document Name"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                variant="outlined"
                                fullWidth
                                className="main-form"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'rgba(22, 46, 60, 0.20)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(22, 46, 60, 0.40)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: LightBlueBtnColor,
                                            borderWidth: '2px',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        height: '28px !important',
                                        padding: '4.5px 14px !important'
                                    },
                                    '& .MuiFormLabel-root': { transform: 'translate(14px, 9px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(20px, -5px) scale(0.75)' } },
                                    // '& .MuiInputLabel-root': {
                                    //     fontFamily: "'Proxima Nova', sans-serif",
                                    //     fontSize: '14px',
                                    // },
                                }}
                            />
                        </FormControl>
                        <Button variant="contained" onClick={handleSearch} size="medium" startIcon={<Search />}
                            sx={{
                                flexShrink: 0,
                                backgroundColor: '#2099d2',
                                color: WhiteColor,
                                boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
                                '&:hover': {
                                    backgroundColor: '#176990',
                                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                                },
                            }}
                        >Search</Button>
                        <Button variant="outlined" onClick={handleClear} startIcon={<Clear />}
                            sx={{
                                flexShrink: 0,
                                borderRadius: '4px',
                                backgroundColor: WhiteColor,
                                color: '#176990',
                                border: '1px solid rgba(25, 118, 210, 0.5)',
                                '&:hover': {
                                    backgroundColor: HoverUnselectedLightBlueBtnColor,
                                    color: '#176990',
                                    border: '1px solid #176990',
                                },
                            }}
                        >Clear</Button>
                    </Box>
                    )}
                </Box>
                
                
                
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ p: { xs: 1, sm: 2 }, borderRadius: '12px', backgroundColor: '#FBFDFE' }}>
                        {diligentSearchLibrary && (<>
                            {/* Show DataGridPro when a folder is selected, otherwise show folder list */}
                            {selectedYear !== null && selectedMonth !== null ? (
                                <Box sx={{ height: '627px', width: '100%', mt: 1 }}>
                                    <DataGridPro rows={documents}
                                        columns={columns}
                                        getRowId={(row) => row.diligentSearchLibraryDocumentId}
                                        checkboxSelection
                                        disableSelectionOnClick
                                        sortModel={sortModel}
                                        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                                        selectionModel={selectedRows.map(row => row.diligentSearchLibraryDocumentId)}
                                        onSelectionModelChange={(newSelectionModel) => {
                                            const selectedDocuments = (newSelectionModel as number[])
                                                .map(id => documents.find(doc => doc.diligentSearchLibraryDocumentId === id))
                                                .filter((doc): doc is DiligentSearchItem => doc !== undefined);
                                            setSelectedRows(selectedDocuments);
                                        }}
                                        components={{
                                            Toolbar: EditToolbar,
                                        }}
                                        sx={{
                                            border: '1px solid rgba(22, 46, 60, 0.12)',
                                            borderRadius: '12px',
                                            backgroundColor: WhiteColor,
                                            '& .MuiDataGrid-root': {
                                                fontFamily: "'Proxima Nova', sans-serif",
                                            },
                                            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                                                color: IsUserClient ? WhiteColor : '#162E3C',
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: IsUserClient ? LightBlueBtnColor : '#f8fafc',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: IsUserClient ? WhiteColor : '#162E3C',
                                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                                                // borderTopLeftRadius: '12px',
                                                // borderTopRightRadius: '12px',
                                            },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                color: IsUserClient ? WhiteColor : '#162E3C',
                                            },
                                            '& .MuiDataGrid-menuIconButton': {
                                                color: IsUserClient ? WhiteColor : '#162E3C',
                                            },
                                            '& .MuiDataGrid-iconButtonContainer button': {
                                                color: IsUserClient ? WhiteColor : '#162E3C',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#F4F8FB',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderColor: 'rgba(22, 46, 60, 0.08)',
                                            },
                                            ...(IsUserClient && selectedRows.length > 0 && {
                                                '& .MuiDataGrid-columnHeader[data-field="__check__"]': {
                                                    color: WhiteColor,
                                                    '& .MuiCheckbox-root': {
                                                        color: WhiteColor,
                                                    },
                                                    '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate': {
                                                        color: WhiteColor,
                                                    },
                                                },
                                            }),
                                        }}
                                    />
                                </Box>
                            ) : (<>
                                {/* Breadcrumb Header - Only show when NOT in folder view */}
                                <Box sx={{ mb: 2, mt: 1 }}>
                                    <BreadcrumbNavigation />
                                </Box>
                                {/* Prior Years Section */}
                                {(() => {
                                    const currentYear = new Date().getFullYear();
                                    
                                    const getMonths = (year: number) => Object.keys(diligentSearchLibrary[year] ?? {}).map(Number)
                                            .filter(m => diligentSearchLibrary[year][m] != null)
                                            .sort((a, b) => a - b);
                                    
                                    const priorYears = Object.keys(diligentSearchLibrary).map(Number)
                                        .filter(y => y < currentYear && getMonths(y).length > 0)
                                        .sort((a, b) => b - a);
                                    
                                    const currentYearMonths = getMonths(currentYear);
                                    
                                    return (<>
                                        {/* Prior Years */}
                                        {priorYears.length > 0 && !selectedPriorYear && (
                                            !priorYearsExpanded ? (
                                                <FolderItem key="prior-years" label="Prior Years" onClick={onSelectPriorYearsFolder} mt={2} />
                                            ) : (
                                                priorYears.map((year, i) => (
                                                    <FolderItem key={year} label={year} onClick={() => onSelectPriorYear(year)}
                                                        selectable
                                                        selected={selectedFolders.some(folder => folder.scope === 'year' && folder.year === year)}
                                                        onSelect={() => handleToggleFolderSelection(year, null, 'year')}
                                                        mt={i === 0 ? 2 : 0}
                                                    />
                                                ))
                                            )
                                        )}
                                        {/* Selected Prior Year Months */}
                                        {selectedPriorYear != null &&
                                            getMonths(selectedPriorYear).map((month, i) =>
                                                <FolderItem key={`${selectedPriorYear}-${month}`}
                                                    label={`${Months[month as keyof typeof Months]} ${selectedPriorYear}`}
                                                    onClick={() => onSelectFolder(selectedPriorYear, month)}
                                                    selectable
                                                    selected={selectedFolders.some(folder => folder.scope === 'month' && folder.year === selectedPriorYear && folder.month === month)}
                                                    onSelect={() => handleToggleFolderSelection(selectedPriorYear, month, 'month')}
                                                    mt={i === 0 ? 2 : 0}
                                                />
                                            )
                                        }
                                        {/* Current Year */}
                                        {!priorYearsExpanded &&
                                            currentYearMonths.map(month =>
                                                <FolderItem key={`${currentYear}-${month}`}
                                                    label={`${Months[month as keyof typeof Months]} ${currentYear}`}
                                                    onClick={() => onSelectFolder(currentYear, month)}
                                                    selectable
                                                    selected={selectedFolders.some(folder => folder.scope === 'month' && folder.year === currentYear && folder.month === month)}
                                                    onSelect={() => handleToggleFolderSelection(currentYear, month, 'month')}
                                                />
                                            )
                                        }
                                    </>);
                                })()}
                            </>)
                            }
                            
                            {diligentSearchLibrary && Object.keys(diligentSearchLibrary).length === 0 && selectedYear === null && (
                                <Typography sx={{ 
                                    color: '#757575',
                                    fontFamily: "'Proxima Nova', sans-serif",
                                    textAlign: 'center',
                                    mt: 4,
                                }}>No folders found</Typography>
                            )}
                            
                            {!diligentSearchLibrary && (
                                <Typography sx={{ 
                                    color: '#757575',
                                    fontFamily: "'Proxima Nova', sans-serif",
                                    textAlign: 'center',
                                    mt: 4,
                                }}>No folders found</Typography>
                            )}
                        </>)}
                    </Box>
                </Box>
            </Box>
            
            <Box sx={{ width: '80%' }}>
                <AddNewDiligentSearchFolder Open={currentView === ViewOptions.CreateFolder} 
                    handleClose={onCloseUploadFolder}
                    currentPartner={selectedPartner}
                />
            </Box>
            
            {/* Document Viewer popup dialog */}
            <DocumentViewer open={documentViewerOpen}
                documents={documentViewerDocuments.map((doc: DiligentSearchItem) => ({
                    documentUrl: doc.blob_Address,
                    documentName: doc.documentName,
                }))}
                onClose={handleCloseDocumentViewer}
            />
            </Box>
        </LocalizationProvider>
    );
};
export default DiligentSearchLibrary;